<script setup>
import {
  NCard, NSpace, NH1, NH2, NH3, NP, NInput, NButton,
  NGradientText, NCollapse, NCollapseItem, NTabs, NTabPane,
  NTag, NAlert, NEmpty
} from 'naive-ui';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  Search, HelpCircle, Users, Scale, MessageCircle,
  CreditCard, Shield, Settings, Phone, Mail,
  BookOpen, Star, Clock, CheckCircle, Languages
} from 'lucide-vue-next';
import { dt } from '@/utils/i18n';

const { t, locale } = useI18n();

const searchQuery = ref('');
const activeTab = ref('general');

// All FAQ data organized by category
const faqData = {
  general: {
    icon: HelpCircle,
    color: '#36d1dc',
    questions: [
      { q: 'faq.general.q1.question', a: 'faq.general.q1.answer' },
      { q: 'faq.general.q2.question', a: 'faq.general.q2.answer' },
      { q: 'faq.general.q3.question', a: 'faq.general.q3.answer' },
      { q: 'faq.general.q4.question', a: 'faq.general.q4.answer' },
      { q: 'faq.general.q5.question', a: 'faq.general.q5.answer' },
      { q: 'faq.general.q6.question', a: 'faq.general.q6.answer' }
    ]
  },
  lawyers: {
    icon: Scale,
    color: '#5b86e5',
    questions: [
      { q: 'faq.lawyers.q1.question', a: 'faq.lawyers.q1.answer' },
      { q: 'faq.lawyers.q2.question', a: 'faq.lawyers.q2.answer' },
      { q: 'faq.lawyers.q3.question', a: 'faq.lawyers.q3.answer' },
      { q: 'faq.lawyers.q4.question', a: 'faq.lawyers.q4.answer' },
      { q: 'faq.lawyers.q5.question', a: 'faq.lawyers.q5.answer' },
      { q: 'faq.lawyers.q6.question', a: 'faq.lawyers.q6.answer' }
    ]
  },
  contact: {
    icon: Phone,
    color: '#ef473a',
    questions: [
      { q: 'faq.contact.q1.question', a: 'faq.contact.q1.answer' },
      { q: 'faq.contact.q2.question', a: 'faq.contact.q2.answer' },
      { q: 'faq.contact.q3.question', a: 'faq.contact.q3.answer' },
      { q: 'faq.contact.q4.question', a: 'faq.contact.q4.answer' },
      { q: 'faq.contact.q5.question', a: 'faq.contact.q5.answer' },
      { q: 'faq.contact.q6.question', a: 'faq.contact.q6.answer' }
    ]
  },
  verification: {
    icon: Shield,
    color: '#f7b733',
    questions: [
      { q: 'faq.verification.q1.question', a: 'faq.verification.q1.answer' },
      { q: 'faq.verification.q2.question', a: 'faq.verification.q2.answer' },
      { q: 'faq.verification.q3.question', a: 'faq.verification.q3.answer' },
      { q: 'faq.verification.q4.question', a: 'faq.verification.q4.answer' },
      { q: 'faq.verification.q5.question', a: 'faq.verification.q5.answer' }
    ]
  },
  languages: {
    icon: Languages,
    color: '#11998e',
    questions: [
      { q: 'faq.languages.q1.question', a: 'faq.languages.q1.answer' },
      { q: 'faq.languages.q2.question', a: 'faq.languages.q2.answer' },
      { q: 'faq.languages.q3.question', a: 'faq.languages.q3.answer' },
      { q: 'faq.languages.q4.question', a: 'faq.languages.q4.answer' },
      { q: 'faq.languages.q5.question', a: 'faq.languages.q5.answer' }
    ]
  },
  account: {
    icon: Settings,
    color: '#9b59b6',
    questions: [
      { q: 'faq.account.q1.question', a: 'faq.account.q1.answer' },
      { q: 'faq.account.q2.question', a: 'faq.account.q2.answer' },
      { q: 'faq.account.q3.question', a: 'faq.account.q3.answer' },
      { q: 'faq.account.q4.question', a: 'faq.account.q4.answer' },
      { q: 'faq.account.q5.question', a: 'faq.account.q5.answer' }
    ]
  }
};

// Popular/Most viewed FAQs
const popularFaqs = [
  { category: 'general', index: 0, views: 1250 },
  { category: 'lawyers', index: 0, views: 1100 },
  { category: 'contact', index: 0, views: 950 },
  { category: 'verification', index: 0, views: 800 },
  { category: 'languages', index: 1, views: 750 }
];

// Filter FAQs based on search
const filteredFaqs = computed(() => {
  if (!searchQuery.value.trim()) {
    return faqData[activeTab.value].questions;
  }
  
  const query = searchQuery.value.toLowerCase();
  return faqData[activeTab.value].questions.filter(faq => 
    dt(faq.q).toLowerCase().includes(query) || 
    dt(faq.a).toLowerCase().includes(query)
  );
});

// Get total FAQ count
const totalFaqs = computed(() => {
  return Object.values(faqData).reduce((total, category) => total + category.questions.length, 0);
});
</script>

<template>
  <div class="faq-container">
    <!-- Hero Section -->
    <div class="hero-section">
      <NH1 class="hero-title">
        <NGradientText :gradient="{ from: '#36d1dc', to: '#5b86e5', deg: 45 }">
          {{ dt('faq.heroTitle') }}
        </NGradientText>
      </NH1>
      <NP class="hero-subtitle">{{ dt('faq.heroSubtitle') }}</NP>
      
      <!-- Search -->
      <div class="search-section">
        <NInput
          v-model:value="searchQuery"
          size="large"
          :placeholder="dt('faq.search.placeholder')"
          class="search-input"
        >
          <template #prefix>
            <Search size="20" />
          </template>
        </NInput>
      </div>
    </div>

    <!-- Statistics -->
    <div class="stats-section">
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-number">{{ totalFaqs }}</div>
          <div class="stat-label">{{ dt('faq.stats.totalQuestions') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">6</div>
          <div class="stat-label">{{ dt('faq.stats.categories') }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">95%</div>
          <div class="stat-label">{{ dt('faq.stats.helpful') }}</div>
        </div>
      </div>
    </div>

    <!-- Popular FAQs -->
    <div class="popular-section">
      <NH2 class="section-title">{{ dt('faq.popularTitle') }}</NH2>
      <div class="popular-grid">
        <div v-for="(popular, index) in popularFaqs" :key="index" class="popular-faq-card">
          <div class="popular-header">
            <component 
              :is="faqData[popular.category].icon" 
              size="20" 
              :color="faqData[popular.category].color" 
            />
            <NTag 
              size="small" 
              :type="index < 2 ? 'success' : 'info'"
            >
              {{ dt(`faq.tabs.${popular.category}`) }}
            </NTag>
            <div class="popular-views">
              <Star size="14" color="#f7b733" />
              <span>{{ popular.views }} {{ dt('faq.views') }}</span>
            </div>
          </div>
          <NH3 class="popular-question">
            {{ dt(faqData[popular.category].questions[popular.index].q) }}
          </NH3>
        </div>
      </div>
    </div>

    <!-- Main FAQ Section -->
    <div class="main-faq-section">
      <NH2 class="section-title">{{ dt('faq.browseTitle') }}</NH2>
      
      <NTabs v-model:value="activeTab" type="line" animated size="large">
        <NTabPane 
          v-for="(category, key) in faqData" 
          :key="key"
          :name="key" 
          :tab="dt(`faq.tabs.${key}`)"
        >
          <template #tab>
            <div class="tab-content">
              <component :is="category.icon" size="18" :color="category.color" />
              <span>{{ dt(`faq.tabs.${key}`) }}</span>
              <NTag size="small" :type="key === 'general' ? 'primary' : 'default'">
                {{ category.questions.length }}
              </NTag>
            </div>
          </template>
          
          <div class="faq-content">
            <div v-if="filteredFaqs.length > 0" class="faqs-list">
              <NCollapse>
                <NCollapseItem
                  v-for="(faq, index) in filteredFaqs"
                  :key="index"
                  :name="index"
                >
                  <template #header>
                    <div class="faq-question-header">
                      <HelpCircle size="16" :color="category.color" />
                      <span>{{ dt(faq.q) }}</span>
                    </div>
                  </template>
                  
                  <div class="faq-answer">
                    <NP>{{ dt(faq.a) }}</NP>
                    <div class="faq-feedback">
                      <span class="feedback-text">{{ dt('faq.helpful') }}</span>
                      <NButton size="small" text type="success">
                        <template #icon><CheckCircle size="16" /></template>
                        {{ dt('faq.yes') }}
                      </NButton>
                      <NButton size="small" text>
                        {{ dt('faq.no') }}
                      </NButton>
                    </div>
                  </div>
                </NCollapseItem>
              </NCollapse>
            </div>
            
            <NEmpty 
              v-else 
              :description="dt('faq.noResults')"
              class="no-results"
            >
              <template #icon>
                <Search size="48" color="#d1d5db" />
              </template>
            </NEmpty>
          </div>
        </NTabPane>
      </NTabs>
    </div>

    <!-- Still Need Help -->
    <NCard class="help-card">
      <div class="help-content">
        <div class="help-text">
          <NH2 class="help-title">{{ dt('faq.stillNeedHelp.title') }}</NH2>
          <NP class="help-description">{{ dt('faq.stillNeedHelp.description') }}</NP>
        </div>
        <div class="help-actions">
          <NButton type="primary" size="large" class="help-button">
            <template #icon><MessageCircle size="18" /></template>
            {{ dt('faq.stillNeedHelp.chat') }}
          </NButton>
          <NButton size="large" class="help-button-secondary">
            <template #icon><Mail size="18" /></template>
            {{ dt('faq.stillNeedHelp.email') }}
          </NButton>
        </div>
      </div>
    </NCard>

    <!-- Quick Contact Alert -->
    <NAlert type="info" class="contact-alert">
      <template #icon>
        <Phone />
      </template>
      {{ dt('faq.quickContact') }}
    </NAlert>
  </div>
</template>

<style scoped>
.faq-container {
  padding: 16px;
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  color: #333;
  overflow-x: hidden;
  box-sizing: border-box;
}

.hero-section {
  text-align: center;
  padding: 40px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  margin-top: 12px;
  color: #555;
  margin-bottom: 32px;
}

.search-section {
  max-width: 500px;
  margin: 0 auto;
}

.section-title {
  text-align: center;
  margin-bottom: 30px;
  font-size: 1.5rem;
  position: relative;
}

.section-title::after {
  content: "";
  display: block;
  width: 50px;
  height: 3px;
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  margin: 10px auto 0;
  border-radius: 2px;
}

.stats-section {
  margin: 60px 0 40px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  max-width: 600px;
  margin: 0 auto;
}

.stat-item {
  text-align: center;
  padding: 20px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
}

.stat-number {
  font-size: 2.5rem;
  font-weight: bold;
  color: #36d1dc;
  margin-bottom: 8px;
}

.stat-label {
  color: #666;
  font-size: 0.9rem;
}

.popular-section {
  margin: 60px 0;
}

.popular-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
}

.popular-faq-card {
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  cursor: pointer;
}

.popular-faq-card:hover {
  transform: translateY(-3px);
}

.popular-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.popular-views {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  color: #666;
  font-size: 0.85rem;
}

.popular-question {
  color: #333;
  font-size: 1rem;
  line-height: 1.5;
}

.main-faq-section {
  margin: 60px 0;
}

.tab-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

.faq-content {
  margin-top: 24px;
}

.faq-question-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
}

.faq-answer {
  padding: 16px 0;
}

.faq-feedback {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #eee;
}

.feedback-text {
  color: #666;
  font-size: 0.9rem;
}

.no-results {
  padding: 40px 20px;
}

.help-card {
  margin: 60px 0;
  border-radius: 16px;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(54, 209, 220, 0.1), rgba(91, 134, 229, 0.1));
}

.help-content {
  display: flex;
  align-items: center;
  gap: 40px;
  padding: 40px;
}

.help-text {
  flex: 1;
}

.help-title {
  margin-bottom: 12px;
  color: #333;
}

.help-description {
  color: #666;
  font-size: 1.1rem;
}

.help-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.help-button {
  background: linear-gradient(135deg, #36d1dc, #5b86e5);
  border: none;
  padding: 0 24px;
  height: 48px;
  min-width: 160px;
}

.help-button-secondary {
  border: 2px solid #36d1dc;
  color: #36d1dc;
  padding: 0 24px;
  height: 48px;
  min-width: 160px;
}

.help-button-secondary:hover {
  background: #36d1dc;
  color: white;
}

.contact-alert {
  margin: 40px 0;
  border-radius: 12px;
}

:deep(.n-collapse-item__header) {
  font-weight: 500;
}

:deep(.n-tabs-nav) {
  margin-bottom: 0;
}

:deep(.n-tabs-pane) {
  padding-top: 0;
}

@media screen and (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .popular-grid {
    grid-template-columns: 1fr;
  }
  
  .help-content {
    flex-direction: column;
    text-align: center;
    gap: 24px;
  }
  
  .help-actions {
    flex-direction: row;
    justify-content: center;
    width: 100%;
  }
  
  .help-button,
  .help-button-secondary {
    flex: 1;
    max-width: 200px;
  }
  
  .tab-content {
    font-size: 0.9rem;
  }
  
  .popular-header {
    flex-wrap: wrap;
    gap: 6px;
  }
  
  .popular-views {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
}

@media screen and (max-width: 480px) {
  .help-actions {
    flex-direction: column;
  }
  
  .help-button,
  .help-button-secondary {
    width: 100%;
  }
  
  .stats-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }
  
  .stat-item {
    padding: 16px 8px;
  }
  
  .stat-number {
    font-size: 2rem;
  }
  
  .stat-label {
    font-size: 0.8rem;
  }
}
</style>